import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Plus, 
  CreditCard,
  Building2,
  Cake,
  Users
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import Modal from '../components/Modal';
import PatientDetail from '../components/PatientDetail';
import EvolutionSearchModal from '../components/EvolutionSearchModal';
import MergePatientsModal from '../components/MergePatientsModal';
import { useLocation } from 'react-router-dom';
import './Patients.css';


class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', background: '#ff000033', color: 'white', borderRadius: '8px', margin: '20px' }}>
          <h2>Error en Patients</h2>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{this.state.error?.toString()}</pre>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '10px', marginTop: '10px' }}>{this.state.error?.stack}</pre>
          <button onClick={() => this.setState({hasError: false})} style={{ marginTop: '10px', padding: '5px 10px' }}>Reintentar</button>
        </div>
      );
    }
    return this.props.children;
  }
}


const PatientsInner = () => {
  const { addNotification } = useNotifications();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPatients, setTotalPatients] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const stored = sessionStorage.getItem('pending_create_patient');
    if (stored) {
      try {
        setInitialData(JSON.parse(stored));
      } catch(e) { /* ignore */ }
      sessionStorage.removeItem('pending_create_patient');
      setIsModalOpen(true);
    } else if (location.state?.createPatient && location.state?.appData) {
      setInitialData(location.state.appData);
      setIsModalOpen(true);
    }
  }, [location.state]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isSearchEvolutionsOpen, setIsSearchEvolutionsOpen] = useState(false);
  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 16;

  // Load active staff from localStorage
  const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
  const staffName = activeStaff.full_name || '';

  const fetchPatients = async () => {
    setLoading(true);
    try {
      let query = supabase.from('patients').select('*', { count: 'exact' });
      
      if (searchTerm && searchTerm.trim()) {
        const terms = searchTerm.trim().split(/\s+/);
        // Build "and" query for full_name to match all terms
        const fullNameQuery = terms.map(t => `full_name.ilike.%${t}%`).join(',');
        const andFullName = `and(${fullNameQuery})`;
        
        const cleanQuery = searchTerm.replace(/[^0-9a-zA-Z]/g, '');
        const wildcardDni = cleanQuery.length > 0 ? cleanQuery.split('').join('%') : searchTerm;

        query = query.or(`${andFullName},dni.ilike.%${searchTerm.trim()}%,dni.ilike.%${wildcardDni}%`);
      } else {
        query = query.order('created_at', { ascending: false });
      }

      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;
      if (error) throw error;
      setPatients(data || []);
      setTotalPatients(count || 0);
    } catch (err) {
      console.error(err);
      addNotification('Error al cargar pacientes', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchPatients();
    }, 300);
    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleAddPatient = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newPatient = {
      full_name: formData.get('full_name'),
      dni: formData.get('dni'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      birthdate: formData.get('birthdate'),
      insurance_provider: formData.get('insurance_provider'),
      medical_notes: formData.get('medical_notes'),
      professional_name: staffName || 'Consulta General',
      affiliate_number: formData.get('affiliate_number'),
      patient_type: formData.get('patient_type') || 'Ambulatorio',
      address: formData.get('address'),
      city: formData.get('city'),
      province: formData.get('province'),
      country: formData.get('country') || 'Argentina'
    };

    try {
      const { error } = await supabase.from('patients').insert([newPatient]);
      if (error) throw error;
      addNotification('Paciente registrado con éxito', 'success');
      setIsModalOpen(false);
      fetchPatients();
    } catch (err) {
      addNotification('Error al registrar: ' + err.message, 'error');
    }
  };

  return (
    <div className="patients-page">
      <div className="patients-top-block">
        <div className="search-box-blip">
          <input 
            type="text" 
            placeholder="Buscar Pacientes: por Nº Doc., o por Apellido/Nombre (al menos 4 letras)" 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Search size={18} className="search-icon-blip" />
        </div>

        <div className="patients-buttons-column">
          <button className="btn-blip-action orange" onClick={() => setIsSearchEvolutionsOpen(true)}>
            <Search size={16} /> BUSCAR EVOLUCIONES
          </button>
          <button className="btn-blip-action green" onClick={() => setIsMergeOpen(true)}>
            <Users size={16} /> UNIFICAR PAC. REPET.
          </button>
          <button className="btn-blip-action blue" onClick={() => setIsModalOpen(true)}>
            NUEVO PACIENTE <Plus size={16} />
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 className="patients-subtitle" style={{ margin: 0, paddingBottom: 0 }}>
          {searchTerm ? 'Resultados de búsqueda' : 'Estos son los últimos 16 pacientes cargados o modificados.'}
        </h3>
        <div style={{ 
          background: 'rgba(59, 130, 246, 0.1)', 
          color: '#60a5fa', 
          padding: '6px 14px', 
          borderRadius: '20px', 
          fontSize: '0.85rem', 
          fontWeight: '600', 
          border: '1px solid rgba(59, 130, 246, 0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <Users size={14} />
          {searchTerm ? `Encontrados: ${totalPatients}` : `Total Registrados: ${totalPatients}`}
        </div>
      </div>

      <div className="patients-grid-blip">
        {loading ? (
          <div className="text-center-p">Procesando base de datos...</div>
        ) : patients.length === 0 ? (
          <div className="no-results-msg">No se encontraron pacientes que coincidan con la búsqueda.</div>
        ) : patients.map(p => (
          <div key={p.id} className="patient-card-blip card" onClick={() => { setSelectedPatient(p); }}>
            <div className="p-card-header">
              <h4>{p.full_name?.toUpperCase()}</h4>
            </div>
            
            <div className="p-card-body">
              <div className="p-info-item">
                <Building2 size={16} className="icon-green" />
                <span>{p.professional_name || 'Sin Especialista'}</span>
              </div>
              <div className="p-info-item">
                <CreditCard size={16} className="icon-red" />
                <span>{p.dni || 'Sin DNI'}</span>
              </div>
              <div className="p-info-item">
                <Cake size={16} className="icon-orange" />
                <span>{p.birthdate ? new Date(p.birthdate + 'T00:00:00').toLocaleDateString('es-AR') : 'Sin Completar'}</span>
              </div>
            </div>

            <div className="p-card-actions">
              <button className="btn-opciones" onClick={(e) => { e.stopPropagation(); setSelectedPatient(p); }}>
                OPCIONES
              </button>
            </div>
          </div>
        ))}
      </div>

      {!loading && totalPatients > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', padding: '15px 20px', background: '#1e293b', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
          <span style={{ color: '#94a3b8', fontSize: '0.95rem' }}>
            Mostrando {(currentPage - 1) * itemsPerPage + 1} a {Math.min(currentPage * itemsPerPage, totalPatients)} de {totalPatients} registros
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{ 
                padding: '8px 16px', 
                borderRadius: '8px', 
                background: currentPage === 1 ? '#0f172a' : '#3b82f6', 
                color: currentPage === 1 ? '#475569' : 'white', 
                border: 'none', 
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
            >
              Anterior
            </button>
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalPatients / itemsPerPage)))}
              disabled={currentPage >= Math.ceil(totalPatients / itemsPerPage)}
              style={{ 
                padding: '8px 16px', 
                borderRadius: '8px', 
                background: currentPage >= Math.ceil(totalPatients / itemsPerPage) ? '#0f172a' : '#3b82f6', 
                color: currentPage >= Math.ceil(totalPatients / itemsPerPage) ? '#475569' : 'white', 
                border: 'none', 
                cursor: currentPage >= Math.ceil(totalPatients / itemsPerPage) ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        title="Registro de Paciente"
      >
        <form onSubmit={handleAddPatient} className="patient-form-premium">
          <div className="form-grid-p">
            <div className="form-group-p full">
              <label>Nombre Completo</label>
              <input name="full_name" type="text" required placeholder="Ej: Juan Pérez" defaultValue={initialData?.patient_name || ""} />
            </div>
            <div className="form-group-p">
              <label>DNI / Cédula</label>
              <input name="dni" type="text" placeholder="Ej: 30.123.456" defaultValue={initialData?.patient_dni || initialData?.study_details?.patient_dni || ""} />
            </div>
            <div className="form-group-p">
              <label>Fecha de Nacimiento</label>
              <input name="birthdate" type="date" defaultValue={initialData?.study_details?.patient_birthdate || ""} />
            </div>
            <div className="form-group-p">
              <label>Teléfono</label>
              <input name="phone" type="tel" placeholder="Ej: +54 9 11..." defaultValue={initialData?.patient_phone || ""} />
            </div>
            <div className="form-group-p">
              <label>Obra Social / Prepaga</label>
              <input name="insurance_provider" type="text" placeholder="Ej: OSDE, Particular..." defaultValue={initialData?.study_details?.insurance_provider || ""} />
            </div>
            <div className="form-group-p">
              <label>Nº de Afiliado</label>
              <input name="affiliate_number" type="text" placeholder="Ej: 123456789" />
            </div>
            <div className="form-group-p full">
              <label>Email</label>
              <input name="email" type="email" placeholder="paciente@correo.com" defaultValue={initialData?.patient_email || ""} />
            </div>
            <div className="form-group-p full">
              <label>Dirección Física</label>
              <input name="address" type="text" placeholder="Calle, Número, Piso, Depto..." />
            </div>
            <div className="form-group-p">
              <label>Ciudad</label>
              <input name="city" type="text" placeholder="Ej: CABA" />
            </div>
            <div className="form-group-p">
              <label>Provincia / Estado</label>
              <input name="province" type="text" placeholder="Ej: Buenos Aires" />
            </div>
            <div className="form-group-p">
              <label>País</label>
              <input name="country" type="text" defaultValue="Argentina" placeholder="Ej: Argentina" />
            </div>
            <div className="form-group-p">
              <label>Tipo de Paciente</label>
              <select name="patient_type" style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: '1px solid var(--border-glass)', background: 'rgba(255,255,255,0.05)', color: 'white' }}>
                <option value="Ambulatorio">Ambulatorio</option>
                <option value="Internado">Internado</option>
                <option value="A Domicilio">A Domicilio</option>
                <option value="Telemedicina">Telemedicina</option>
              </select>
            </div>
            <div className="form-group-p full">
              <label>Observaciones Iniciales / Antecedentes</label>
              <textarea name="medical_notes" rows="3" placeholder="Alergias, medicación crónica..."></textarea>
            </div>
          </div>
          <div className="form-footer-p">
            <button type="button" className="btn-cancel-modal" onClick={() => setIsModalOpen(false)}>CANCELAR</button>
            <button type="submit" className="btn-save-p">GUARDAR FICHA</button>
          </div>
        </form>
      </Modal>

      {selectedPatient && (
        <PatientDetail 
          patient={selectedPatient} 
          onClose={() => {
            setSelectedPatient(null);
            fetchPatients();
          }} 
        />
      )}

      <EvolutionSearchModal 
        isOpen={isSearchEvolutionsOpen}
        onClose={() => setIsSearchEvolutionsOpen(false)}
        onSelectPatient={(p) => setSelectedPatient(p)}
      />

      <MergePatientsModal 
        isOpen={isMergeOpen}
        onClose={() => setIsMergeOpen(false)}
        onMergeSuccess={fetchPatients}
      />
    </div>
  );
};


const Patients = (props) => (
  <ErrorBoundary>
    <PatientsInner {...props} />
  </ErrorBoundary>
);

export default Patients;
